import { request } from '@/data/request';
import type { TeleattendanceVideoConnection } from '@/views/teleattendance/services/teleattendanceService';

export type OnboardingInterviewContext = 'establishment' | 'company';
export type OnboardingInterviewStatus =
  | 'pending_approval'
  | 'assigned'
  | 'in_progress'
  | 'completed'
  | 'cancelled'
  | 'request_rejected';
export type OnboardingInterviewResult = 'approved' | 'rejected';
export type InterviewParticipantModerationAction =
  | 'mute_microphone'
  | 'disable_camera'
  | 'allow_microphone'
  | 'allow_camera'
  | 'disable_screen_share'
  | 'allow_screen_share'
  | 'disconnect'
  | 'remove';

export interface OnboardingInterviewQueueItem {
  onboarding_id: number;
  professional_id: number;
  status: string;
  professional_name: string;
  email?: string;
  phone?: string;
  created_at?: string;
  updated_at?: string;
  rule_tags?: ProfessionalRuleTag[];
}

export interface ProfessionalRuleTag {
  id?: number;
  ID?: number;
  code: string;
  source: string;
  severity: string;
  title: string;
  message: string;
  metadata?: string;
  detected_at?: string;
}

export interface OnboardingInterview {
  id: number;
  onboarding_id: number;
  professional_id: number;
  interviewer_user_id: number;
  interview_availability_id?: number;
  status: OnboardingInterviewStatus;
  result?: OnboardingInterviewResult;
  meeting_room_name?: string;
  meeting_url?: string;
  notes?: string;
  cancellation_reason?: string;
  scheduled_start_at?: string;
  scheduled_end_at?: string;
  started_at?: string;
  held_at?: string;
  completed_at?: string;
  completed_by_user_id?: number;
  evaluation_justification?: string;
  evaluated_by_user_id?: number;
  evaluated_at?: string;
  cancelled_at?: string;
  participant_removed_at?: string;
  participant_removal_reason?: string;
  meeting_participant_limit?: number;
  professional_name?: string;
  interviewer_name?: string;
}

export interface OnboardingInterviewProfessionalProfile {
  interview_id: number;
  onboarding_id: number;
  professional_id: number;
  interview_status: OnboardingInterviewStatus;
  name?: string;
  email?: string;
  phone?: string;
  cpf?: string;
  cns?: string;
  birth_date?: string;
  sex?: string;
  council_number?: string;
  council_state?: string;
  council_name?: string;
  registration_type?: string;
  professional_category?: string;
  workload?: number;
  morning_shift?: boolean;
  afternoon_shift?: boolean;
  evening_shift?: boolean;
  is_statutory?: boolean;
  enabled?: boolean;
  last_council_sync_at?: string;
  cep?: string;
  address?: string;
  number?: string;
  neighborhood?: string;
  city?: string;
  complement?: string;
  specialities?: string[];
  rule_tags?: ProfessionalRuleTag[];
}

export interface OnboardingInterviewProfessionalDocumentUpload {
  id: number;
  file_name: string;
  mime_type?: string;
  size?: number;
  side?: string;
  status?: string;
  status_id?: number;
  document_format?: string;
  presigned_url?: string;
  rejection_reason?: string;
  validity_date?: string;
  created_at?: string;
  updated_at?: string;
}

export interface OnboardingInterviewProfessionalDocument {
  document_id: number;
  document_type_id: number;
  document_type_name: string;
  required?: boolean;
  front_back?: boolean;
  status?: string;
  status_id?: number;
  missing?: boolean;
  is_complete?: boolean;
  uploads?: OnboardingInterviewProfessionalDocumentUpload[];
}

export interface OnboardingInterviewAvailability {
  id: number;
  interviewer_user_id: number;
  status: 'available' | 'booked' | 'cancelled';
  starts_at: string;
  ends_at: string;
  capacity: number;
  booked_count: number;
  pending_count: number;
  remaining_capacity: number;
  booked_interview_id?: number;
  booked_at?: string;
  cancelled_at?: string;
  interviewer_name?: string;
}

export interface OnboardingInterviewPublicLink {
  public_token: string;
  public_path: string;
  public_url?: string;
  landing_path?: string;
  landing_url?: string;
  meeting_link?: string;
  app_link?: string;
  email_sent?: boolean;
  email_error?: string;
}

export type OnboardingInterviewRecordingStatus =
  | 'starting'
  | 'active'
  | 'ending'
  | 'complete'
  | 'failed'
  | 'aborted'
  | 'limit_reached'
  | 'unknown';

export interface OnboardingInterviewRecording {
  id: number;
  interview_id: number;
  egress_id: string;
  room_name: string;
  status: OnboardingInterviewRecordingStatus;
  error?: string;
  download_url?: string;
  started_at?: string;
  stopped_at?: string;
  completed_at?: string;
}

export interface OnboardingInterviewParticipantLimit {
  limit: number;
  automatic_limit: number;
  effective_limit: number;
  active_count: number;
  max_limit: number;
}

export interface OnboardingInterviewRoomCandidate {
  interview: OnboardingInterview;
  public_link: OnboardingInterviewPublicLink;
}

const contextParams = (context: OnboardingInterviewContext) => ({ context });

const videoConnectionFromResponse = (
  data: any,
): TeleattendanceVideoConnection => ({
  provider: String(data.provider || 'livekit'),
  serverUrl: String(data.server_url || ''),
  roomName: String(data.room_name || ''),
  participantIdentity: String(data.participant_identity || ''),
  participantName: String(data.participant_name || ''),
  participantMetadata: String(data.participant_metadata || ''),
  scheduledStartAt: data.scheduled_start_at
    ? String(data.scheduled_start_at)
    : undefined,
  scheduledEndAt: data.scheduled_end_at
    ? String(data.scheduled_end_at)
    : undefined,
  startedAt: data.started_at ? String(data.started_at) : undefined,
  token: String(data.token || ''),
  chatAccessToken: data.chat_access_token
    ? String(data.chat_access_token)
    : undefined,
  chatAccessTokenExpiresAt: data.chat_access_token_expires_at
    ? String(data.chat_access_token_expires_at)
    : undefined,
});

export interface OnboardingInterviewChatAttachment {
  id: number;
  file_name: string;
  mime_type: string;
  size: number;
  is_image: boolean;
  status: 'pending' | 'attached';
}

export type OnboardingInterviewChatAudience =
  | 'room'
  | 'interviewer'
  | 'candidate';

export interface OnboardingInterviewChatMessage {
  id: number;
  sender_type: 'interviewer' | 'candidate';
  sender_name: string;
  sender_identity: string;
  audience: OnboardingInterviewChatAudience;
  recipient_interview_id?: number;
  recipient_name?: string;
  content: string;
  client_message_id: string;
  created_at: string;
  attachments: OnboardingInterviewChatAttachment[];
}

export interface OnboardingInterviewChatAccess {
  isPublic: boolean;
  interviewId?: number;
  context?: OnboardingInterviewContext;
  accessToken?: string;
  accessTokenExpiresAt?: string;
}

const chatRequestHeaders = (access: OnboardingInterviewChatAccess) =>
  access.isPublic && access.accessToken
    ? { 'X-Meeting-Access-Token': access.accessToken }
    : undefined;

const chatBasePath = (access: OnboardingInterviewChatAccess) => {
  if (access.isPublic) return '/onboarding-public/interviews/chat';
  return `/onboarding/interviews/${Number(access.interviewId)}/chat`;
};

const chatContextQuery = (access: OnboardingInterviewChatAccess) =>
  access.isPublic || !access.context ? '' : `?context=${access.context}`;

export const listOnboardingInterviewChatMessages = async (
  access: OnboardingInterviewChatAccess,
  beforeId?: number,
): Promise<OnboardingInterviewChatMessage[]> => {
  const response = await request<{ data: OnboardingInterviewChatMessage[] }>({
    method: 'get',
    url: `${chatBasePath(access)}/messages`,
    data: {
      ...(access.context && !access.isPublic
        ? { context: access.context }
        : {}),
      limit: 50,
      ...(beforeId ? { before_id: beforeId } : {}),
    },
    config: { headers: chatRequestHeaders(access) },
  });
  return response.data || [];
};

export const createOnboardingInterviewChatMessage = async (
  access: OnboardingInterviewChatAccess,
  content: string,
  attachmentIds: number[],
  clientMessageId: string,
  audience: OnboardingInterviewChatAudience = 'room',
  recipientInterviewId?: number,
): Promise<OnboardingInterviewChatMessage> => {
  const response = await request<{ data: OnboardingInterviewChatMessage }>({
    method: 'post',
    url: `${chatBasePath(access)}/messages${chatContextQuery(access)}`,
    data: {
      content,
      attachment_ids: attachmentIds,
      client_message_id: clientMessageId,
      audience,
      ...(recipientInterviewId
        ? { recipient_interview_id: recipientInterviewId }
        : {}),
    },
    config: { headers: chatRequestHeaders(access) },
  });
  return response.data;
};

export const uploadOnboardingInterviewChatAttachment = async (
  access: OnboardingInterviewChatAccess,
  file: File,
  onProgress?: (percent: number) => void,
): Promise<OnboardingInterviewChatAttachment> => {
  const body = new FormData();
  body.append('file', file);
  const response = await request<{ data: OnboardingInterviewChatAttachment }>({
    method: 'post',
    url: `${chatBasePath(access)}/attachments${chatContextQuery(access)}`,
    data: body,
    config: { headers: chatRequestHeaders(access) },
    onUploadProgress: (event) => {
      const total = Number(event.total || 0);
      if (total > 0) onProgress?.(Math.round((event.loaded / total) * 100));
    },
  });
  return response.data;
};

export const deleteOnboardingInterviewChatAttachment = async (
  access: OnboardingInterviewChatAccess,
  attachmentId: number,
): Promise<void> => {
  await request({
    method: 'delete',
    url: `${chatBasePath(access)}/attachments/${attachmentId}${chatContextQuery(access)}`,
    config: { headers: chatRequestHeaders(access) },
  });
};

export const fetchOnboardingInterviewChatAttachment = async (
  access: OnboardingInterviewChatAccess,
  attachmentId: number,
): Promise<Blob> => {
  return request<Blob>({
    method: 'get',
    url: `${chatBasePath(access)}/attachments/${attachmentId}/content`,
    data:
      access.context && !access.isPublic
        ? { context: access.context }
        : undefined,
    config: {
      headers: chatRequestHeaders(access),
      responseType: 'blob',
    },
  });
};

export const refreshPublicOnboardingInterviewChatToken = async (
  accessToken: string,
): Promise<{
  chat_access_token: string;
  chat_access_token_expires_at: string;
}> => {
  const response = await request<{
    data: {
      chat_access_token: string;
      chat_access_token_expires_at: string;
    };
  }>({
    method: 'post',
    url: '/onboarding-public/interviews/chat/token/refresh',
    data: {},
    config: { headers: { 'X-Meeting-Access-Token': accessToken } },
  });
  return response.data;
};

const onboardingInterviewClient = () => {
  if (typeof window === 'undefined') return '';
  const isMobile =
    window.matchMedia?.('(max-width: 767px)').matches ||
    window.matchMedia?.('(hover: none)').matches ||
    window.matchMedia?.('(pointer: coarse)').matches ||
    /Android|iPhone|iPad|iPod|Mobile/i.test(window.navigator.userAgent);
  return isMobile ? 'mobile_web' : '';
};

export const listOnboardingInterviewQueue = async (
  context: OnboardingInterviewContext,
): Promise<OnboardingInterviewQueueItem[]> => {
  const response = await request<{ data: OnboardingInterviewQueueItem[] }>({
    method: 'get',
    url: '/onboarding/interviews/queue',
    data: contextParams(context),
  });
  return response.data || [];
};

export const listMyOnboardingInterviews = async (
  context: OnboardingInterviewContext,
): Promise<OnboardingInterview[]> => {
  const response = await request<{ data: OnboardingInterview[] }>({
    method: 'get',
    url: '/onboarding/interviews/mine',
    data: contextParams(context),
  });
  return response.data || [];
};

export const listOnboardingInterviewHistory = async (
  context: OnboardingInterviewContext,
): Promise<OnboardingInterview[]> => {
  const response = await request<{ data: OnboardingInterview[] }>({
    method: 'get',
    url: '/onboarding/interviews/history',
    data: contextParams(context),
  });
  return response.data || [];
};

export const listMyOnboardingInterviewAvailabilities = async (
  context: OnboardingInterviewContext,
): Promise<OnboardingInterviewAvailability[]> => {
  const response = await request<{ data: OnboardingInterviewAvailability[] }>({
    method: 'get',
    url: '/onboarding/interviews/availabilities',
    data: contextParams(context),
  });
  return response.data || [];
};

export const createOnboardingInterviewAvailability = async (
  context: OnboardingInterviewContext,
  startsAt: string,
  endsAt: string,
  slotDurationMinutes: number,
  capacity: number,
): Promise<OnboardingInterviewAvailability[]> => {
  const response = await request<{ data: OnboardingInterviewAvailability[] }>({
    method: 'post',
    url: `/onboarding/interviews/availabilities?context=${context}`,
    data: {
      starts_at: startsAt,
      ends_at: endsAt,
      slot_duration_minutes: slotDurationMinutes,
      capacity,
    },
  });
  return response.data || [];
};

export const cancelOnboardingInterviewAvailability = async (
  context: OnboardingInterviewContext,
  availabilityId: number,
): Promise<void> => {
  await request({
    method: 'delete',
    url: `/onboarding/interviews/availabilities/${availabilityId}?context=${context}`,
  });
};

export const startOnboardingInterview = async (
  context: OnboardingInterviewContext,
  professionalId: number,
): Promise<OnboardingInterview> => {
  const response = await request<{ data: OnboardingInterview }>({
    method: 'post',
    url: `/onboarding/interviews/${professionalId}/start?context=${context}`,
    data: {},
  });
  return response.data;
};

export const releaseOnboardingInterview = async (
  context: OnboardingInterviewContext,
  interviewId: number,
): Promise<OnboardingInterview> => {
  const response = await request<{ data: OnboardingInterview }>({
    method: 'post',
    url: `/onboarding/interviews/${interviewId}/release?context=${context}`,
    data: {},
  });
  return response.data;
};

export const reopenOnboardingInterview = async (
  context: OnboardingInterviewContext,
  interviewId: number,
): Promise<OnboardingInterview> => {
  const response = await request<{ data: OnboardingInterview }>({
    method: 'post',
    url: `/onboarding/interviews/${interviewId}/reopen?context=${context}`,
    data: {},
  });
  return response.data;
};

export const completeOnboardingInterview = async (
  context: OnboardingInterviewContext,
  interviewId: number,
  notes: string,
  evaluation?: {
    result: 'approved' | 'rejected';
    justification?: string;
  },
): Promise<OnboardingInterview> => {
  const response = await request<{ data: OnboardingInterview }>({
    method: 'post',
    url: `/onboarding/interviews/${interviewId}/complete?context=${context}`,
    data: { notes, ...evaluation },
  });
  return response.data;
};

export const updateOnboardingInterviewNotes = async (
  context: OnboardingInterviewContext,
  interviewId: number,
  notes: string,
): Promise<OnboardingInterview> => {
  const response = await request<{ data: OnboardingInterview }>({
    method: 'patch',
    url: `/onboarding/interviews/${interviewId}/notes?context=${context}`,
    data: { notes },
  });
  return response.data;
};

export const getOnboardingInterviewProfessionalProfile = async (
  context: OnboardingInterviewContext,
  interviewId: number,
): Promise<OnboardingInterviewProfessionalProfile> => {
  const response = await request<{
    data: OnboardingInterviewProfessionalProfile;
  }>({
    method: 'get',
    url: `/onboarding/interviews/${interviewId}/professional-profile`,
    data: contextParams(context),
  });
  return response.data;
};

export const getOnboardingInterviewProfessionalDocuments = async (
  context: OnboardingInterviewContext,
  interviewId: number,
): Promise<OnboardingInterviewProfessionalDocument[]> => {
  const response = await request<{
    data: OnboardingInterviewProfessionalDocument[];
  }>({
    method: 'get',
    url: `/onboarding/interviews/${interviewId}/professional-documents`,
    data: contextParams(context) as any,
  });
  return response.data || [];
};

export const decideOnboardingInterviewRequest = async (
  context: OnboardingInterviewContext,
  interviewId: number,
  decision: 'approved' | 'rejected',
  reason = '',
): Promise<OnboardingInterview> => {
  const response = await request<{ data: OnboardingInterview }>({
    method: 'post',
    url: `/onboarding/interviews/${interviewId}/request-decision?context=${context}`,
    data: { decision, reason },
  });
  return response.data;
};

export const fetchOnboardingInterviewVideoConnection = async (
  context: OnboardingInterviewContext,
  interviewId: number,
): Promise<TeleattendanceVideoConnection> => {
  const response = await request<{ data: any }>({
    method: 'get',
    url: `/onboarding/interviews/${interviewId}/video-connection`,
    data: contextParams(context),
  });
  return videoConnectionFromResponse(response.data || {});
};

export const fetchOnboardingInterviewPublicLink = async (
  context: OnboardingInterviewContext,
  interviewId: number,
): Promise<OnboardingInterviewPublicLink> => {
  const response = await request<{ data: OnboardingInterviewPublicLink }>({
    method: 'get',
    url: `/onboarding/interviews/${interviewId}/public-link`,
    data: contextParams(context),
  });
  return response.data;
};

export const fetchPublicOnboardingInterviewVideoConnection = async (
  token: string,
  cpf: string,
  birthDate: string,
): Promise<TeleattendanceVideoConnection> => {
  const normalizedBirthDate = birthDate.includes('/')
    ? birthDate.split('/').reverse().join('-')
    : birthDate;
  const response = await request<{ data: any }>({
    method: 'post',
    url: `/onboarding-public/interviews/${token}/video-connection`,
    data: {
      cpf,
      birth_date: normalizedBirthDate,
      client: onboardingInterviewClient(),
    },
  });
  return videoConnectionFromResponse(response.data || {});
};

export const fetchRecordingOnboardingInterviewVideoConnection = async (
  token: string,
): Promise<TeleattendanceVideoConnection> => {
  const response = await request<{ data: any }>({
    method: 'get',
    url: `/onboarding-public/interviews/recording/${token}/video-connection`,
  });
  return videoConnectionFromResponse(response.data || {});
};

export const moderateOnboardingInterviewParticipant = async (
  context: OnboardingInterviewContext,
  interviewId: number,
  participantIdentity: string,
  action: InterviewParticipantModerationAction,
  reason?: string,
): Promise<void> => {
  await request({
    method: 'post',
    url: `/onboarding/interviews/${interviewId}/participants/moderate?context=${context}`,
    data: {
      participant_identity: participantIdentity,
      action,
      reason,
    },
  });
};

export const endOnboardingInterviewMeeting = async (
  context: OnboardingInterviewContext,
  interviewId: number,
): Promise<OnboardingInterview> => {
  const response = await request<{ data: OnboardingInterview }>({
    method: 'post',
    url: `/onboarding/interviews/${interviewId}/end-meeting?context=${context}`,
    data: {},
  });
  return response.data;
};

export const fetchOnboardingInterviewParticipantLimit = async (
  context: OnboardingInterviewContext,
  interviewId: number,
): Promise<OnboardingInterviewParticipantLimit> => {
  const response = await request<{ data: OnboardingInterviewParticipantLimit }>(
    {
      method: 'get',
      url: `/onboarding/interviews/${interviewId}/participant-limit`,
      data: contextParams(context),
    },
  );
  return response.data;
};

export const updateOnboardingInterviewParticipantLimit = async (
  context: OnboardingInterviewContext,
  interviewId: number,
  payload: { limit?: number; delta?: number },
): Promise<OnboardingInterviewParticipantLimit> => {
  const response = await request<{ data: OnboardingInterviewParticipantLimit }>(
    {
      method: 'patch',
      url: `/onboarding/interviews/${interviewId}/participant-limit?context=${context}`,
      data: payload,
    },
  );
  return response.data;
};

export const addOnboardingInterviewRoomCandidate = async (
  context: OnboardingInterviewContext,
  interviewId: number,
  professionalId: number,
): Promise<OnboardingInterviewRoomCandidate> => {
  const response = await request<{ data: OnboardingInterviewRoomCandidate }>({
    method: 'post',
    url: `/onboarding/interviews/${interviewId}/room-candidates?context=${context}`,
    data: { professional_id: professionalId },
  });
  return response.data;
};

export const fetchOnboardingInterviewRecording = async (
  context: OnboardingInterviewContext,
  interviewId: number,
  includeDownloadURL = false,
): Promise<OnboardingInterviewRecording | null> => {
  try {
    const response = await request<{ data: OnboardingInterviewRecording }>({
      method: 'get',
      url: `/onboarding/interviews/${interviewId}/recording?context=${context}&download_url=${includeDownloadURL ? 'true' : 'false'}`,
    });
    return response.data || null;
  } catch (error: any) {
    if (error?.response?.status === 404) return null;
    throw error;
  }
};

export const listOnboardingInterviewRecordings = async (
  context: OnboardingInterviewContext,
  interviewId: number,
  includeDownloadURL = false,
): Promise<OnboardingInterviewRecording[]> => {
  const response = await request<{ data: OnboardingInterviewRecording[] }>({
    method: 'get',
    url: `/onboarding/interviews/${interviewId}/recordings?context=${context}&download_url=${includeDownloadURL ? 'true' : 'false'}`,
  });
  return response.data || [];
};

export const fetchOnboardingInterviewRecordingDownload = async (
  context: OnboardingInterviewContext,
  interviewId: number,
  recordingId: number,
): Promise<OnboardingInterviewRecording> => {
  const response = await request<{ data: OnboardingInterviewRecording }>({
    method: 'get',
    url: `/onboarding/interviews/${interviewId}/recordings/${recordingId}/download?context=${context}`,
  });
  return response.data;
};

export const startOnboardingInterviewRecording = async (
  context: OnboardingInterviewContext,
  interviewId: number,
): Promise<OnboardingInterviewRecording> => {
  const response = await request<{ data: OnboardingInterviewRecording }>({
    method: 'post',
    url: `/onboarding/interviews/${interviewId}/recording/start?context=${context}`,
    data: {},
  });
  return response.data;
};

export const stopOnboardingInterviewRecording = async (
  context: OnboardingInterviewContext,
  interviewId: number,
): Promise<OnboardingInterviewRecording> => {
  const response = await request<{ data: OnboardingInterviewRecording }>({
    method: 'post',
    url: `/onboarding/interviews/${interviewId}/recording/stop?context=${context}`,
    data: {},
  });
  return response.data;
};
